import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { Badge, Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap'
import { PHASE3_PRODUCT_SCOPES } from '../constants'
import { formatPhase3MoneyInput, sanitizePhase3MoneyInput } from '../helpers/phase3-inbound-journey-helper'

const propTypes = {
  products             : PropTypes.array,
  isLoading            : PropTypes.bool,
  onClickRow           : PropTypes.func,
  getProductUrl        : PropTypes.func,
  scope                : PropTypes.string,
  onSubmitAppraisal    : PropTypes.func,
  onConfirmBuyingPrice : PropTypes.func,
  onRejectBuyingPrice  : PropTypes.func,
  onApproveAppraisal     : PropTypes.func,
  onBulkApproveAppraisal : PropTypes.func,
  onRejectAppraisal      : PropTypes.func,
  onManualGoLive         : PropTypes.func,
  submittingProductId    : PropTypes.oneOfType([ PropTypes.number, PropTypes.string ]),
  isBulkApproving         : PropTypes.bool,
  onAppraisalDirtyChange  : PropTypes.func,
  apiNamespace           : PropTypes.string
}

const defaultProps = {
  products             : [],
  isLoading            : false,
  onClickRow           : () => {},
  getProductUrl        : () => '',
  scope                : '',
  onSubmitAppraisal    : () => {},
  onConfirmBuyingPrice : () => {},
  onRejectBuyingPrice  : () => {},
  onApproveAppraisal     : () => {},
  onBulkApproveAppraisal : () => {},
  onRejectAppraisal      : () => {},
  onManualGoLive         : () => {},
  submittingProductId    : null,
  isBulkApproving         : false,
  onAppraisalDirtyChange  : () => {},
  apiNamespace           : ''
}

const modalHeaderStyle = { position: 'relative', justifyContent: 'center', textAlign: 'center', paddingLeft: 56, paddingRight: 56 }
const modalTitleStyle = { width: '100%', textAlign: 'center' }
const modalFooterStyle = { justifyContent: 'center' }
const modalCloseButtonStyle = { position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%)', margin: 0 }
const actionButtonWrapperStyle = { gap: 8 }
const appraisalPriceFieldStyle = { minWidth: 132, width: 132 }
const appraisalSkuStyle = { minWidth: 180, width: 180, whiteSpace: 'nowrap' }
const appraisalSkuButtonStyle = { whiteSpace: 'nowrap', lineHeight: 1.25 }
const appraisalConditionStyle = { minWidth: 115 }
const appraisalProductNameStyle = { minWidth: 210 }
const appraisalConsignorStyle = { minWidth: 160 }
const appraisalBatchStyle = { minWidth: 160, width: 160, maxWidth: 160, whiteSpace: 'nowrap' }
const appraisalActionStyle = { minWidth: 88, width: 88, textAlign: 'center', verticalAlign: 'middle' }

const confirmBuyingPriceSkuStyle = { minWidth: 170, width: 170, whiteSpace: 'nowrap' }
const confirmBuyingPriceSkuButtonStyle = { whiteSpace: 'nowrap', lineHeight: 1.25 }
const confirmBuyingPriceConditionStyle = { minWidth: 110, width: 110 }
const confirmBuyingPriceProductNameStyle = { minWidth: 190, width: 190 }
const confirmBuyingPriceConsignorStyle = { minWidth: 145, width: 145 }
const confirmBuyingPricePriceStyle = { minWidth: 118, width: 118 }
const confirmBuyingPriceBatchStyle = { minWidth: 145, width: 145, whiteSpace: 'nowrap' }
const confirmBuyingPriceActionStyle = { minWidth: 128, width: 128, textAlign: 'center', verticalAlign: 'middle' }

const appraisalApprovalCheckboxStyle = { minWidth: 42, width: 42, textAlign: 'center', verticalAlign: 'middle' }
const appraisalApprovalSkuStyle = { minWidth: 170, width: 170, whiteSpace: 'nowrap' }
const appraisalApprovalSkuButtonStyle = { whiteSpace: 'nowrap', lineHeight: 1.25 }
const appraisalApprovalConditionStyle = { minWidth: 110, width: 110 }
const appraisalApprovalProductNameStyle = { minWidth: 210, width: 210 }
const appraisalApprovalConsignorStyle = { minWidth: 145, width: 145 }
const appraisalApprovalPriceStyle = { minWidth: 118, width: 118 }
const appraisalApprovalBatchStyle = { minWidth: 145, width: 145, whiteSpace: 'nowrap' }
const appraisalApprovalActionStyle = { minWidth: 150, width: 150, textAlign: 'center', verticalAlign: 'middle' }
const queueSkuStyle = { minWidth: 160, width: 160, whiteSpace: 'nowrap' }
const queueProductNameStyle = { minWidth: 360 }


const formatThousandSeparatedPrice = (value) => {
  const number = parseInt(value || 0, 10)
  if (Number.isNaN(number) || number <= 0) return '-'

  return number.toLocaleString('id-ID')
}

const renderCompactReadOnlyPrice = (value) => (
  <div className='bg-light text-muted rounded px-2 py-1'>
    { formatThousandSeparatedPrice(value) }
  </div>
)

const getInventoryBatch = (product = {}) => {
  const batch = product.consignment?.inventory_batch || product.inbound?.inventory_batch || {}
  const batchCode = product.consignment?.inventory_batch_code || product.inbound?.inventory_batch_code || batch.batch_code || '-'

  return {
    id   : batch.id || product.consignment?.inventory_batch_id || product.inbound?.inbound_journey_inventory_batch_id,
    code : batchCode
  }
}

const renderInventoryBatch = (product = {}, apiNamespace = '') => {
  const batch = getInventoryBatch(product)
  if (!batch.code || batch.code === '-') return <div className='font-weight-bold'>-</div>

  const params = new URLSearchParams()
  if (batch.id) params.set('batch_id', batch.id)
  else params.set('q', batch.code)
  if (apiNamespace) params.set('api_namespace', apiNamespace)
  const batchListPath = apiNamespace === 'all_team'
    ? '/app/all-team/inbound-journey/inventory-batches'
    : '/app/inbound-journey/inventory-batches'

  return (
    <a
      className='font-weight-bold'
      href={ `${batchListPath}?${params.toString()}` }
      target='_blank'
      rel='noopener noreferrer'
    >
      { batch.code }
    </a>
  )
}

const renderCenteredModalCloseButton = (onClick) => (
  <button type='button' className='close' aria-label='Close' onClick={ onClick } style={ modalCloseButtonStyle }>
    <span aria-hidden='true'>×</span>
  </button>
)

const getConsignorName = (product = {}) => {
  return product.consignment?.consignor_detail?.formatted_name ||
    product.consignment?.consignor_detail?.name ||
    product.consignment?.consignor?.name ||
    product.supplier_name ||
    '-'
}

const getConsignorUserId = (product = {}) => (
  product.consignor_user_id ||
  product.seller_id ||
  product.consignment?.consignor_detail?.user_id ||
  product.consignment?.consignor?.id ||
  product.consignment?.consignor_id ||
  product.consignor_id ||
  product.user_id
)

const renderConsignorName = (product = {}) => {
  const name = getConsignorName(product)
  const userId = getConsignorUserId(product)
  if (!userId || name === '-') return name

  return (
    <a
      href={ `/app/sales/users/${userId}` }
      className='font-weight-bold'
      target='_blank'
      rel='noopener noreferrer'
    >
      { name } #{ userId }
    </a>
  )
}

const queueMissingFieldsForScope = (product = {}, scope = '') => {
  if (scope === PHASE3_PRODUCT_SCOPES.STUDIO) return product.inbound_journey_missing_studio_fields || []
  if (scope === PHASE3_PRODUCT_SCOPES.PRICE_TAG) return product.inbound_journey_missing_price_tag_fields || []
  if ([ PHASE3_PRODUCT_SCOPES.READY_TO_LIVE, PHASE3_PRODUCT_SCOPES.READY_FOR_INBOUND, PHASE3_PRODUCT_SCOPES.LIVE ].includes(scope)) return product.inbound_journey_missing_live_fields || []

  return [
    ...(product.inbound_journey_missing_studio_fields || []),
    ...(product.inbound_journey_missing_price_tag_fields || []),
    ...(product.inbound_journey_missing_live_fields || [])
  ]
}

const hasCompleteAppraisalPrices = (product = {}) => {
  if (typeof product.appraisal_prices_complete === 'boolean') return product.appraisal_prices_complete

  return [ product.buying_price, product.default_price, product.selling_price ].every((value) => {
    return value != null && Number(value) > 0
  })
}

const isAppraisalApprovalEligible = (product = {}, appraisalForm = product) => {
  return product.can_approve_appraisal !== false &&
    product.appraisal_state === 'need_approval' &&
    hasCompleteAppraisalPrices(appraisalForm)
}

const hasPositiveAppraisalPrice = (value) => {
  return value != null && value.toString().trim() !== '' && Number(value) > 0
}

const buildAppraisalForm = (product = {}) => ({
  buying_price              : product.buying_price == null ? '' : product.buying_price,
  default_price             : product.default_price == null ? '' : product.default_price,
  selling_price             : product.selling_price == null ? '' : product.selling_price,
  appraisal_note            : product.appraisal_note || '',
  skip_confirm_buying_price : false
})

const APPRAISAL_MONEY_FIELDS = [ 'buying_price', 'default_price', 'selling_price' ]

const normalizeAppraisalMoney = (value) => {
  if (value == null || value.toString().trim() === '') return ''

  const number = parseInt(value, 10)
  return Number.isNaN(number) ? value.toString().trim() : number.toString()
}

const appraisalFormChanged = (product, form) => {
  const initialForm = buildAppraisalForm(product)
  const moneyChanged = APPRAISAL_MONEY_FIELDS.some((field) => {
    return normalizeAppraisalMoney(form[field]) !== normalizeAppraisalMoney(initialForm[field])
  })
  const noteChanged = (form.appraisal_note || '').toString().trim() !==
    (initialForm.appraisal_note || '').toString().trim()

  return moneyChanged || noteChanged
}

const Phase3ProductQueueTable = ({
  products,
  isLoading,
  onClickRow,
  getProductUrl,
  scope,
  onSubmitAppraisal,
  onConfirmBuyingPrice,
  onRejectBuyingPrice,
  onApproveAppraisal,
  onBulkApproveAppraisal,
  onRejectAppraisal,
  onManualGoLive,
  submittingProductId,
  isBulkApproving,
  onAppraisalDirtyChange,
  apiNamespace
}) => {
  const [ appraisalForms, setAppraisalForms ] = useState({})
  const [ pendingBuyingPriceConfirm, setPendingBuyingPriceConfirm ] = useState(null)
  const [ pendingBuyingPriceReject, setPendingBuyingPriceReject ] = useState(null)
  const [ buyingPriceRejectNote, setBuyingPriceRejectNote ] = useState('')
  const [ buyingPriceRejectWarning, setBuyingPriceRejectWarning ] = useState('')
  const [ pendingAppraisalApprove, setPendingAppraisalApprove ] = useState(null)
  const [ pendingBulkAppraisalApprove, setPendingBulkAppraisalApprove ] = useState(null)
  const [ selectedAppraisalIds, setSelectedAppraisalIds ] = useState([])
  const [ pendingAppraisalReject, setPendingAppraisalReject ] = useState(null)
  const [ appraisalRejectNote, setAppraisalRejectNote ] = useState('')
  const [ appraisalRejectWarning, setAppraisalRejectWarning ] = useState('')
  const isAppraisalFill = scope === PHASE3_PRODUCT_SCOPES.APPRAISAL_NEEDED
  const isConfirmBuyingPrice = scope === PHASE3_PRODUCT_SCOPES.CONFIRM_BUYING_PRICE
  const isAppraisalApproval = scope === PHASE3_PRODUCT_SCOPES.APPRAISAL_APPROVAL
  const isReadyToLive = [ PHASE3_PRODUCT_SCOPES.READY_TO_LIVE, PHASE3_PRODUCT_SCOPES.READY_FOR_INBOUND ].includes(scope) && apiNamespace !== 'all_team'

  useEffect(() => {
    if (!isAppraisalFill && !isAppraisalApproval) return

    const nextForms = {}
    products.forEach((product) => {
      nextForms[product.id] = buildAppraisalForm(product)
    })
    setAppraisalForms(nextForms)
  }, [ products, isAppraisalFill, isAppraisalApproval ])

  useEffect(() => {
    if (!isAppraisalApproval) return

    const eligibleIds = products
      .filter((product) => isAppraisalApprovalEligible(product, appraisalForms[product.id] || buildAppraisalForm(product)))
      .map((product) => product.id)

    setSelectedAppraisalIds((currentIds) => currentIds.filter((id) => eligibleIds.includes(id)))
  }, [ products, appraisalForms, isAppraisalApproval ])

  useEffect(() => {
    if (!isAppraisalFill && !isAppraisalApproval) {
      onAppraisalDirtyChange(false)
      return
    }

    const hasChanges = products.some((product) => {
      const form = appraisalForms[product.id] || buildAppraisalForm(product)
      return appraisalFormChanged(product, form)
    })

    onAppraisalDirtyChange(hasChanges)
  }, [ appraisalForms, products, isAppraisalFill, isAppraisalApproval, onAppraisalDirtyChange ])

  if (isLoading) {
    return <div className='p-4 text-center text-muted'>Memuat produk...</div>
  }

  if (!products || products.length === 0) {
    return <div className='p-4 text-center text-muted'>Produk tidak ditemukan.</div>
  }

  const renderProductSku = (product, style) => {
    const productUrl = getProductUrl(product)

    if (!productUrl) {
      return (
        <Button
          type='button'
          color='link'
          className='p-0 font-weight-bold text-left'
          style={ style }
          onClick={ () => onClickRow(product) }
        >
          { product.sku || '-' }
        </Button>
      )
    }

    return (
      <a
        href={ productUrl }
        className='font-weight-bold'
        style={ style }
        target='_blank'
        rel='noopener noreferrer'
      >
        { product.sku || '-' }
      </a>
    )
  }

  const getAppraisalForm = (product) => appraisalForms[product.id] || buildAppraisalForm(product)

  const setAppraisalField = (product, field, value) => {
    setAppraisalForms((prev) => {
      const currentForm = prev[product.id] || buildAppraisalForm(product)
      const nextForm = {
        ...currentForm,
        [field]: value
      }

      const shouldFillSellingPrice = field === 'default_price' && (
        (!isAppraisalApproval && product.group_code === 'BKR') ||
        product.selling_price == null ||
        Number(product.selling_price) <= 0
      )

      if (shouldFillSellingPrice) nextForm.selling_price = value

      return {
        ...prev,
        [product.id]: nextForm
      }
    })
  }

  const setAppraisalMoneyField = (product, field, value) => {
    setAppraisalField(product, field, sanitizePhase3MoneyInput(value))
  }

  const openBuyingPriceConfirmModal = (product) => {
    setPendingBuyingPriceConfirm(product)
  }

  const closeBuyingPriceConfirmModal = () => {
    setPendingBuyingPriceConfirm(null)
  }

  const confirmBuyingPriceSubmit = () => {
    if (!pendingBuyingPriceConfirm) return

    onConfirmBuyingPrice(pendingBuyingPriceConfirm)
    closeBuyingPriceConfirmModal()
  }

  const openBuyingPriceRejectModal = (product) => {
    setPendingBuyingPriceReject(product)
    setBuyingPriceRejectNote(product.appraisal_note || '')
    setBuyingPriceRejectWarning('')
  }

  const closeBuyingPriceRejectModal = () => {
    setPendingBuyingPriceReject(null)
    setBuyingPriceRejectNote('')
    setBuyingPriceRejectWarning('')
  }

  const confirmBuyingPriceReject = () => {
    const note = buyingPriceRejectNote.toString().trim()

    if (!pendingBuyingPriceReject) return

    if (!note) {
      setBuyingPriceRejectWarning('Reject note wajib diisi sebelum reject CF Buying Price.')
      return
    }

    onRejectBuyingPrice(pendingBuyingPriceReject, note)
    closeBuyingPriceRejectModal()
  }

  const openAppraisalApproveModal = (product) => {
    setPendingAppraisalApprove(product)
  }

  const closeAppraisalApproveModal = () => {
    setPendingAppraisalApprove(null)
  }

  const confirmAppraisalApprove = () => {
    if (!pendingAppraisalApprove) return

    onApproveAppraisal(pendingAppraisalApprove, getAppraisalForm(pendingAppraisalApprove))
    closeAppraisalApproveModal()
  }

  const eligibleAppraisalIds = products
    .filter((product) => isAppraisalApprovalEligible(product, getAppraisalForm(product)))
    .map((product) => product.id)
  const allEligibleAppraisalsSelected = eligibleAppraisalIds.length > 0 &&
    eligibleAppraisalIds.every((id) => selectedAppraisalIds.includes(id))

  const toggleAppraisalSelection = (productId) => {
    setSelectedAppraisalIds((currentIds) => {
      if (currentIds.includes(productId)) {
        return currentIds.filter((id) => id !== productId)
      }

      return [ ...currentIds, productId ]
    })
  }

  const toggleAllEligibleAppraisals = () => {
    setSelectedAppraisalIds(allEligibleAppraisalsSelected ? [] : eligibleAppraisalIds)
  }

  const openBulkAppraisalApproveModal = () => {
    const selectedProducts = products.filter((product) => selectedAppraisalIds.includes(product.id))
    if (selectedProducts.length === 0) return

    setPendingBulkAppraisalApprove(selectedProducts)
  }

  const closeBulkAppraisalApproveModal = () => {
    setPendingBulkAppraisalApprove(null)
  }

  const confirmBulkAppraisalApprove = () => {
    if (!pendingBulkAppraisalApprove || pendingBulkAppraisalApprove.length === 0) return

    onBulkApproveAppraisal(pendingBulkAppraisalApprove.map((product) => {
      const form = getAppraisalForm(product)

      return {
        id            : product.id,
        buying_price  : form.buying_price,
        default_price : form.default_price,
        selling_price : form.selling_price
      }
    }))
    closeBulkAppraisalApproveModal()
  }

  const openAppraisalRejectModal = (product) => {
    setPendingAppraisalReject(product)
    setAppraisalRejectNote(product.appraisal_note || '')
    setAppraisalRejectWarning('')
  }

  const closeAppraisalRejectModal = () => {
    setPendingAppraisalReject(null)
    setAppraisalRejectNote('')
    setAppraisalRejectWarning('')
  }

  const confirmAppraisalReject = () => {
    const note = appraisalRejectNote.toString().trim()

    if (!pendingAppraisalReject) return

    if (!note) {
      setAppraisalRejectWarning('Reject note wajib diisi sebelum reject appraisal.')
      return
    }

    onRejectAppraisal(pendingAppraisalReject, note)
    closeAppraisalRejectModal()
  }

  if (isAppraisalApproval) {
    return (
      <>
        <div className='d-flex justify-content-between align-items-center flex-wrap px-3 py-2 border-bottom'>
          <div className='text-muted mb-2 mb-md-0'>
            <strong>{ selectedAppraisalIds.length }</strong> item selected
          </div>
          <Button
            color='success'
            size='sm'
            disabled={ selectedAppraisalIds.length === 0 || isBulkApproving }
            onClick={ openBulkAppraisalApproveModal }
          >
            { isBulkApproving ? 'Approving...' : `Bulk Approve (${selectedAppraisalIds.length})` }
          </Button>
        </div>

        <Table responsive hover size='sm' className='mb-0' style={{ minWidth: 1410 }}>
          <thead>
            <tr>
              <th style={ appraisalApprovalCheckboxStyle }>
                <input
                  type='checkbox'
                  aria-label='Select all eligible appraisal products on this page'
                  checked={ allEligibleAppraisalsSelected }
                  disabled={ eligibleAppraisalIds.length === 0 || isBulkApproving }
                  onChange={ toggleAllEligibleAppraisals }
                />
              </th>
              <th style={ appraisalApprovalSkuStyle }>SKU</th>
              <th style={ appraisalApprovalConditionStyle }>Condition</th>
              <th style={ appraisalApprovalProductNameStyle }>Product Name</th>
              <th style={ appraisalApprovalConsignorStyle }>Consignor Name</th>
              <th style={ appraisalApprovalPriceStyle }>Buying Price</th>
              <th style={ appraisalApprovalPriceStyle }>Default Price</th>
              <th style={ appraisalApprovalPriceStyle }>Selling Price</th>
              <th style={ appraisalApprovalBatchStyle }>Batch</th>
              <th style={ appraisalApprovalActionStyle }>Action</th>
            </tr>
          </thead>
          <tbody>
            { products.map((product) => {
              const isSubmittingRow = submittingProductId === product.id
              const isPendingApproval = product.appraisal_state === 'need_approval'
              const isSelected = selectedAppraisalIds.includes(product.id)
              const form = getAppraisalForm(product)
              const isApprovalEligible = isAppraisalApprovalEligible(product, form)

              return (
                <tr key={ product.id }>
                  <td style={ appraisalApprovalCheckboxStyle }>
                    <input
                      type='checkbox'
                      aria-label={ `Select ${product.sku || 'product'} for bulk approval` }
                      checked={ isSelected }
                      disabled={ !isApprovalEligible || isSubmittingRow || isBulkApproving }
                      onChange={ () => toggleAppraisalSelection(product.id) }
                    />
                  </td>
                  <td style={ appraisalApprovalSkuStyle }>
                    { renderProductSku(product, appraisalApprovalSkuButtonStyle) }
                  </td>
                  <td style={ appraisalApprovalConditionStyle }>{ product.condition_name || '-' }</td>
                  <td style={ appraisalApprovalProductNameStyle }>
                    <div className='font-weight-bold'>{ product.name || '-' }</div>
                    { product.product_curator_pic && <small className='text-muted d-block'>PC: { product.product_curator_pic }</small> }
                  </td>
                  <td style={ appraisalApprovalConsignorStyle }>{ renderConsignorName(product) }</td>
                  <td style={ appraisalApprovalPriceStyle }>
                    <Input
                      bsSize='sm'
                      type='text'
                      inputMode='numeric'
                      disabled={ isSubmittingRow || isBulkApproving || hasPositiveAppraisalPrice(product.buying_price) }
                      value={ formatPhase3MoneyInput(form.buying_price) }
                      placeholder='Enter price'
                      onChange={ (event) => setAppraisalMoneyField(product, 'buying_price', event.target.value) }
                    />
                  </td>
                  <td style={ appraisalApprovalPriceStyle }>
                    <Input
                      bsSize='sm'
                      type='text'
                      inputMode='numeric'
                      disabled={ isSubmittingRow || isBulkApproving || hasPositiveAppraisalPrice(product.default_price) }
                      value={ formatPhase3MoneyInput(form.default_price) }
                      placeholder='Enter price'
                      onChange={ (event) => setAppraisalMoneyField(product, 'default_price', event.target.value) }
                    />
                  </td>
                  <td style={ appraisalApprovalPriceStyle }>
                    <Input
                      bsSize='sm'
                      type='text'
                      inputMode='numeric'
                      disabled={ isSubmittingRow || isBulkApproving || product.can_edit_selling_price === false || hasPositiveAppraisalPrice(product.selling_price) }
                      value={ formatPhase3MoneyInput(form.selling_price) }
                      placeholder='Enter price'
                      onChange={ (event) => setAppraisalMoneyField(product, 'selling_price', event.target.value) }
                    />
                  </td>
                  <td style={ appraisalApprovalBatchStyle }>{ renderInventoryBatch(product, apiNamespace) }</td>
                  <td style={ appraisalApprovalActionStyle }>
                    <div className='d-flex justify-content-center flex-nowrap' style={ actionButtonWrapperStyle }>
                      { isPendingApproval ? (
                        <>
                          <Button
                            color='success'
                            size='sm'
                            disabled={ isSubmittingRow || isBulkApproving || !isApprovalEligible }
                            title={ product.can_approve_appraisal === false ? 'Anda tidak memiliki akses approve untuk product ini' : 'Harga wajib lengkap sebelum approve' }
                            onClick={ () => openAppraisalApproveModal(product) }
                          >
                            { isSubmittingRow ? 'Saving...' : 'Approve' }
                          </Button>
                          <Button
                            color='danger'
                            outline
                            size='sm'
                            disabled={ isSubmittingRow || isBulkApproving }
                            onClick={ () => openAppraisalRejectModal(product) }
                          >
                            Reject
                          </Button>
                        </>
                      ) : (
                        <Badge color='warning'>Needs Revision</Badge>
                      ) }
                    </div>
                  </td>
                </tr>
              )
            }) }
          </tbody>
        </Table>

        <Modal isOpen={ !!pendingAppraisalApprove } toggle={ closeAppraisalApproveModal } centered>
          <ModalHeader close={ renderCenteredModalCloseButton(closeAppraisalApproveModal) } style={ modalHeaderStyle }>
            <span style={ modalTitleStyle }>Approve Appraisal?</span>
          </ModalHeader>
          <ModalBody className='text-center'>
            <div className='mb-3'>
              Pastikan hasil appraisal dan seluruh harga sudah sesuai sebelum approve.
            </div>
            { pendingAppraisalApprove && (
              <div className='text-muted'>
                <div className='mb-2'><strong>SKU:</strong> { pendingAppraisalApprove.sku || '-' }</div>
                <div className='mb-2'><strong>Buying Price:</strong> { formatThousandSeparatedPrice(getAppraisalForm(pendingAppraisalApprove).buying_price) }</div>
                <div className='mb-2'><strong>Default Price:</strong> { formatThousandSeparatedPrice(getAppraisalForm(pendingAppraisalApprove).default_price) }</div>
                <div><strong>Selling Price:</strong> { formatThousandSeparatedPrice(getAppraisalForm(pendingAppraisalApprove).selling_price) }</div>
              </div>
            ) }
          </ModalBody>
          <ModalFooter style={ modalFooterStyle }>
            <Button color='secondary' outline onClick={ closeAppraisalApproveModal }>Cancel</Button>
            <Button color='success' onClick={ confirmAppraisalApprove } disabled={ submittingProductId === pendingAppraisalApprove?.id }>Approve</Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={ !!pendingBulkAppraisalApprove } toggle={ closeBulkAppraisalApproveModal } centered>
          <ModalHeader close={ renderCenteredModalCloseButton(closeBulkAppraisalApproveModal) } style={ modalHeaderStyle }>
            <span style={ modalTitleStyle }>Bulk Approve Appraisal?</span>
          </ModalHeader>
          <ModalBody className='text-center'>
            <div className='mb-3'>
              { pendingBulkAppraisalApprove?.length || 0 } selected products will be approved together.
            </div>
            <div className='text-muted'>
              { (pendingBulkAppraisalApprove || []).slice(0, 5).map((product) => (
                <div key={ product.id }>{ product.sku || '-' }</div>
              )) }
              { (pendingBulkAppraisalApprove?.length || 0) > 5 && (
                <small>and { pendingBulkAppraisalApprove.length - 5 } more products</small>
              ) }
            </div>
          </ModalBody>
          <ModalFooter style={ modalFooterStyle }>
            <Button color='secondary' outline onClick={ closeBulkAppraisalApproveModal }>Cancel</Button>
            <Button color='success' onClick={ confirmBulkAppraisalApprove } disabled={ isBulkApproving }>
              { isBulkApproving ? 'Approving...' : 'Approve Selected' }
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={ !!pendingAppraisalReject } toggle={ closeAppraisalRejectModal } centered>
          <ModalHeader close={ renderCenteredModalCloseButton(closeAppraisalRejectModal) } style={ modalHeaderStyle }>
            <span style={ modalTitleStyle }>Reject Appraisal?</span>
          </ModalHeader>
          <ModalBody>
            <div className='text-center mb-3'>
              Product akan dikembalikan ke Appraisal Needed untuk direvisi.
            </div>
            { pendingAppraisalReject && (
              <div className='text-muted text-center mb-3'><strong>SKU:</strong> { pendingAppraisalReject.sku || '-' }</div>
            ) }
            <Input
              type='textarea'
              rows='4'
              value={ appraisalRejectNote }
              placeholder='Isi reject note untuk revisi appraisal'
              onChange={ (event) => {
                setAppraisalRejectNote(event.target.value)
                setAppraisalRejectWarning('')
              } }
            />
            { appraisalRejectWarning && <small className='text-danger'>{ appraisalRejectWarning }</small> }
          </ModalBody>
          <ModalFooter style={ modalFooterStyle }>
            <Button color='secondary' outline onClick={ closeAppraisalRejectModal }>Cancel</Button>
            <Button color='danger' onClick={ confirmAppraisalReject } disabled={ submittingProductId === pendingAppraisalReject?.id }>Reject</Button>
          </ModalFooter>
        </Modal>
      </>
    )
  }

  if (isConfirmBuyingPrice) {
    return (
      <>
        <Table responsive hover size='sm' className='mb-0' style={{ minWidth: 1260 }}>
          <thead>
            <tr>
              <th style={ confirmBuyingPriceSkuStyle }>SKU</th>
              <th style={ confirmBuyingPriceConditionStyle }>Condition</th>
              <th style={ confirmBuyingPriceProductNameStyle }>Product Name</th>
              <th style={ confirmBuyingPriceConsignorStyle }>Consignor Name</th>
              <th style={ confirmBuyingPricePriceStyle }>Buying Price</th>
              <th style={ confirmBuyingPricePriceStyle }>Default Price</th>
              <th style={ confirmBuyingPricePriceStyle }>Selling Price</th>
              <th style={ confirmBuyingPriceBatchStyle }>Batch</th>
              <th style={ confirmBuyingPriceActionStyle }>Action</th>
            </tr>
          </thead>
          <tbody>
            { products.map((product) => {
              const isSubmittingRow = submittingProductId === product.id

              return (
                <tr key={ product.id }>
                  <td style={ confirmBuyingPriceSkuStyle }>
                    { renderProductSku(product, confirmBuyingPriceSkuButtonStyle) }
                  </td>
                  <td style={ confirmBuyingPriceConditionStyle }>{ product.condition_name || '-' }</td>
                  <td style={ confirmBuyingPriceProductNameStyle }>
                    <div className='font-weight-bold'>{ product.name || '-' }</div>
                    { product.product_curator_pic && <small className='text-muted d-block'>PC: { product.product_curator_pic }</small> }
                  </td>
                  <td style={ confirmBuyingPriceConsignorStyle }>{ renderConsignorName(product) }</td>
                  <td style={ confirmBuyingPricePriceStyle }>{ renderCompactReadOnlyPrice(product.buying_price) }</td>
                  <td style={ confirmBuyingPricePriceStyle }>{ renderCompactReadOnlyPrice(product.default_price) }</td>
                  <td style={ confirmBuyingPricePriceStyle }>{ renderCompactReadOnlyPrice(product.selling_price) }</td>
                  <td style={ confirmBuyingPriceBatchStyle }>{ renderInventoryBatch(product, apiNamespace) }</td>
                  <td style={ confirmBuyingPriceActionStyle }>
                    <div className='d-flex justify-content-center flex-nowrap' style={ actionButtonWrapperStyle }>
                      <Button color='success' size='sm' disabled={ isSubmittingRow } onClick={ () => openBuyingPriceConfirmModal(product) }>
                        { isSubmittingRow ? 'Saving...' : 'Confirm' }
                      </Button>
                      <Button color='danger' outline size='sm' disabled={ isSubmittingRow } onClick={ () => openBuyingPriceRejectModal(product) }>
                        Reject
                      </Button>
                    </div>
                  </td>
                </tr>
              )
            }) }
          </tbody>
        </Table>

        <Modal isOpen={ !!pendingBuyingPriceConfirm } toggle={ closeBuyingPriceConfirmModal } centered>
          <ModalHeader close={ renderCenteredModalCloseButton(closeBuyingPriceConfirmModal) } style={ modalHeaderStyle }>
            <span style={ modalTitleStyle }>Confirm CF Buying Price?</span>
          </ModalHeader>
          <ModalBody className='text-center'>
            <div className='mb-3'>
              Pastikan buying price dan harga lain sudah sesuai sebelum confirm.
            </div>
            { pendingBuyingPriceConfirm && (
              <div className='text-muted'>
                <div className='mb-2'>
                  <strong>SKU:</strong> { pendingBuyingPriceConfirm.sku || '-' }
                </div>
                <div className='mb-2'>
                  <strong>Buying Price:</strong> { formatThousandSeparatedPrice(pendingBuyingPriceConfirm.buying_price) }
                </div>
                <div className='mb-2'>
                  <strong>Default Price:</strong> { formatThousandSeparatedPrice(pendingBuyingPriceConfirm.default_price) }
                </div>
                <div>
                  <strong>Selling Price:</strong> { formatThousandSeparatedPrice(pendingBuyingPriceConfirm.selling_price) }
                </div>
              </div>
            ) }
          </ModalBody>
          <ModalFooter style={ modalFooterStyle }>
            <Button color='secondary' outline onClick={ closeBuyingPriceConfirmModal }>
              Cancel
            </Button>
            <Button color='success' onClick={ confirmBuyingPriceSubmit } disabled={ submittingProductId === pendingBuyingPriceConfirm?.id }>
              Confirm
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={ !!pendingBuyingPriceReject } toggle={ closeBuyingPriceRejectModal } centered>
          <ModalHeader close={ renderCenteredModalCloseButton(closeBuyingPriceRejectModal) } style={ modalHeaderStyle }>
            <span style={ modalTitleStyle }>Reject CF Buying Price?</span>
          </ModalHeader>
          <ModalBody>
            <div className='text-center mb-3'>
              Product akan dikembalikan ke Appraisal Needed untuk direvisi.
            </div>
            { pendingBuyingPriceReject && (
              <div className='text-muted text-center mb-3'>
                <strong>SKU:</strong> { pendingBuyingPriceReject.sku || '-' }
              </div>
            ) }
            <Input
              type='textarea'
              rows='4'
              value={ buyingPriceRejectNote }
              placeholder='Isi reject note untuk curator'
              onChange={ (event) => {
                setBuyingPriceRejectNote(event.target.value)
                setBuyingPriceRejectWarning('')
              } }
            />
            { buyingPriceRejectWarning && <small className='text-danger'>{ buyingPriceRejectWarning }</small> }
          </ModalBody>
          <ModalFooter style={ modalFooterStyle }>
            <Button color='secondary' outline onClick={ closeBuyingPriceRejectModal }>
              Cancel
            </Button>
            <Button color='danger' onClick={ confirmBuyingPriceReject } disabled={ submittingProductId === pendingBuyingPriceReject?.id }>
              Reject
            </Button>
          </ModalFooter>
        </Modal>
      </>
    )
  }

  if (isAppraisalFill) {
    const showSellingPriceColumn = products.some((product) => product.can_view_selling_price !== false)
    const appraisalTableMinWidth = showSellingPriceColumn ? 1370 : 1225

    return (
      <>
        <Table responsive hover size='sm' className='mb-0' style={{ minWidth: appraisalTableMinWidth }}>
          <thead>
            <tr>
              <th style={ appraisalSkuStyle }>SKU</th>
              <th>Condition</th>
              <th>Product Name</th>
              <th>Consignor Name</th>
              <th>Buying Price</th>
              <th>Default Price</th>
              { showSellingPriceColumn && <th>Selling Price</th> }
              <th style={ appraisalBatchStyle }>Batch</th>
              <th style={ appraisalActionStyle }>Action</th>
            </tr>
          </thead>
          <tbody>
            { products.map((product) => {
              const form = getAppraisalForm(product)
              const isSubmittingRow = submittingProductId === product.id
              const canFillAppraisal = product.can_fill_appraisal !== false
              const canViewSellingPrice = product.can_view_selling_price !== false
              const canEditSellingPrice = product.can_edit_selling_price !== false

              return (
                <tr key={ product.id }>
                  <td style={ appraisalSkuStyle }>
                    { renderProductSku(product, appraisalSkuButtonStyle) }
                  </td>
                  <td style={ appraisalConditionStyle }>
                    { product.condition_name || '-' }
                  </td>
                  <td style={ appraisalProductNameStyle }>
                    <div className='font-weight-bold'>{ product.name || '-' }</div>
                    { product.product_curator_pic && <small className='text-muted d-block'>PC: { product.product_curator_pic }</small> }
                  </td>
                  <td style={ appraisalConsignorStyle }>{ renderConsignorName(product) }</td>
                  <td style={ appraisalPriceFieldStyle }>
                    <Input
                      bsSize='sm'
                      type='text'
                      inputMode='numeric'
                      disabled={ !canFillAppraisal || isSubmittingRow }
                      value={ formatPhase3MoneyInput(form.buying_price) }
                      placeholder='Enter price'
                      onChange={ (event) => setAppraisalMoneyField(product, 'buying_price', event.target.value) }
                    />
                  </td>
                  <td style={ appraisalPriceFieldStyle }>
                    <Input
                      bsSize='sm'
                      type='text'
                      inputMode='numeric'
                      disabled={ !canFillAppraisal || isSubmittingRow }
                      value={ formatPhase3MoneyInput(form.default_price) }
                      placeholder='Enter price'
                      onChange={ (event) => setAppraisalMoneyField(product, 'default_price', event.target.value) }
                    />
                  </td>
                  { showSellingPriceColumn && (
                    <td style={ appraisalPriceFieldStyle }>
                      { canViewSellingPrice ? (
                        <Input
                          bsSize='sm'
                          type='text'
                          inputMode='numeric'
                          disabled={ !canFillAppraisal || isSubmittingRow || !canEditSellingPrice }
                          value={ formatPhase3MoneyInput(form.selling_price) }
                          placeholder='Enter price'
                          onChange={ (event) => setAppraisalMoneyField(product, 'selling_price', event.target.value) }
                        />
                      ) : '-' }
                    </td>
                  ) }
                  <td style={ appraisalBatchStyle }>{ renderInventoryBatch(product, apiNamespace) }</td>
                  <td style={ appraisalActionStyle }>
                    <Button
                      color='primary'
                      size='sm'
                      disabled={ isSubmittingRow || !canFillAppraisal }
                      title={ canFillAppraisal ? '' : 'Appraisal group ini hanya dapat diedit oleh team yang ditentukan.' }
                      onClick={ () => onSubmitAppraisal(product, form) }
                    >
                      { isSubmittingRow ? 'Saving...' : 'Submit' }
                    </Button>
                  </td>
                </tr>
              )
            }) }
          </tbody>
        </Table>
      </>
    )
  }

  return (
    <Table responsive hover size='sm' className='mb-0' style={{ minWidth: 1040 }}>
      <thead>
        <tr>
          <th style={ queueSkuStyle }>SKU</th>
          <th>Condition</th>
          <th>Product Name</th>
          <th>Consignor Name</th>
          <th>Batch</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        { products.map((product) => {
          const missing = queueMissingFieldsForScope(product, scope)
          const showInlineMissingInfo = scope !== PHASE3_PRODUCT_SCOPES.STUDIO

          return (
            <tr key={ product.id }>
              <td style={ queueSkuStyle }>
                { renderProductSku(product, { whiteSpace: 'nowrap', lineHeight: 1.25 }) }
              </td>
              <td>{ product.condition_name || '-' }</td>
              <td style={ queueProductNameStyle }>
                <div className='font-weight-bold'>{ product.name || '-' }</div>
                { product.product_curator_pic && <small className='text-muted d-block'>PC: { product.product_curator_pic }</small> }
                { showInlineMissingInfo && missing.length > 0 && (
                  <div className='mt-1'>
                    <Badge color='warning'>{ missing.length } belum lengkap</Badge>
                    <small className='ml-2 text-muted'>{ missing.slice(0, 4).join(', ') }</small>
                  </div>
                ) }
              </td>
              <td>{ renderConsignorName(product) }</td>
              <td>{ renderInventoryBatch(product, apiNamespace) }</td>
              <td className='text-right'>
                { isReadyToLive && (
                  <Button
                    color='success'
                    size='sm'
                    className='mr-2'
                    disabled={ submittingProductId === product.id || missing.length > 0 }
                    title={ missing.length > 0 ? `Lengkapi dahulu: ${missing.join(', ')}` : 'Live Sekarang' }
                    aria-label={ `Make ${product.sku || 'product'} live now` }
                    onClick={ () => onManualGoLive(product) }
                  >
                    { submittingProductId === product.id ? <i className='fas fa-spinner fa-spin' /> : <i className='fas fa-check' /> }
                  </Button>
                ) }
                <Button color='primary' size='sm' onClick={ () => onClickRow(product) }>
                  Buka
                </Button>
              </td>
            </tr>
          )
        }) }
      </tbody>
    </Table>
  )
}

Phase3ProductQueueTable.propTypes = propTypes
Phase3ProductQueueTable.defaultProps = defaultProps

export default Phase3ProductQueueTable
